// Teammate directory (UX spec 6.10). Presence is shown only where the API shares it.
import { Eye, Phone } from 'lucide-react';
import { canViewAsUser } from '../../state/viewAs.ts';
import { Icon } from '../../ui/Icon.tsx';
import { ErrorPanel, SkeletonRows, StatePanel } from '../../ui/StatePanel.tsx';
import { useWorkspace } from '../../state/workspace.tsx';
import { hasPermission } from '../../lib/capabilities.ts';
import { initials } from '../../lib/format.ts';
import { presenceText, presenceTone } from '../phone/PhoneDock.tsx';
import { InvitationsPanel } from './Invitations.tsx';

export function TeamPage() {
  const ws = useWorkspace();
  if (!hasPermission(ws.permissions, 'users:read')) {
    return (
      <div className="page">
        <h1 className="page-title">Team</h1>
        <StatePanel variant="denied" title="You don't have access to the team directory" body="Your role doesn't include listing teammates." details={{ permission: 'users:read' }} />
      </div>
    );
  }
  const agents = new Map((ws.agents.value ?? []).map(a => [a.user_id, a]));
  const users = ws.roster.value ?? [];
  const canCall = hasPermission(ws.permissions, 'calls:create') && !ws.session.view_as;
  return (
    <div className="page">
      <h1 className="page-title">Team</h1>
      {/* Owners and administrators; the server decides what they may actually do. */}
      {hasPermission(ws.permissions, 'users:roles') ? <InvitationsPanel /> : null}
      {hasPermission(ws.permissions, 'users:roles') ? <h2 className="section-title">People</h2> : null}
      {ws.roster.loading ? <SkeletonRows label="Loading team" /> : ws.roster.error ? <ErrorPanel error={ws.roster.error} /> : users.length <= 1 ? (
        <StatePanel variant="empty" title="No teammates yet" body="People appear here when an administrator invites them to this workspace." />
      ) : (
        <ul className="team-grid" aria-label="Teammates">
          {users.map(u => {
            const a = agents.get(u.id);
            const self = u.id === ws.userId;
            return (
              <li key={u.id} className="team-card">
                <span className="avatar" aria-hidden="true">{initials(u.display_name)}</span>
                <span className="team-name">{u.display_name}{self ? ' (you)' : ''}</span>
                <span className="team-meta">{a ? <span className={`dot dot-${presenceTone(a.availability, a.device_online)}`} aria-hidden="true" /> : null}{a ? presenceText(a.availability, a.device_online) : 'Presence not shared with you'}</span>
                <span className="team-meta">{u.active ? u.role_ids.join(', ') : 'Inactive'}</span>
                {!self && canCall && u.active ? (
                  <button type="button" className="btn btn-small" onClick={() => ws.requestDial({ kind: 'internal', userId: u.id })} title="Fills in the dialer. You still press Call.">
                    <Icon icon={Phone} /> Call
                  </button>
                ) : <span />}
                {canViewAsUser(ws.session, u) ? (
                  <button type="button" className="btn btn-small" onClick={() => void ws.startViewAs?.(u.id)}>
                    <Icon icon={Eye} /> View as<span className="visually-hidden"> {u.display_name}</span>
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
